import type { DataTableMode } from '@odo/shared-ui';
import type { DmMessageInboxOrder, DmMessageInboxSort } from '../../../services/dmMessagesInbox';
import type { DmInboxStatus } from '../../../services/dmNotifications';
import { decodeUrlArray, encodeUrlArray } from '../../../hooks/useUrlArrayParam';
import {
    DEFAULT_INBOX_STATUS,
    parseInboxStatus,
    sortForStatus,
    type ClosedSortRule
} from '../shared/inboxStatus';
import { parseInboxSearch, pickListSearch } from '../shared/listRouting';
import type { ListViewRoutes } from '../shared/useListViewNavigation';

export const MESSAGES_LIST_PATH = '/monitoring/messages';

export const MESSAGE_LIST_PARAM = {
    search: 'search',
    practiceIds: 'practiceIds',
    assignees: 'assignees',
    tags: 'tags',
    status: 'status',
    sort: 'sort',
    order: 'order',
    mode: 'mode'
} as const;

type MessageListParamKey = (typeof MESSAGE_LIST_PARAM)[keyof typeof MESSAGE_LIST_PARAM];

const MESSAGE_LIST_PARAM_KEYS: readonly string[] = Object.values(MESSAGE_LIST_PARAM);

export const DEFAULT_MESSAGE_SORT: DmMessageInboxSort = 'latest';
const DEFAULT_ORDER: DmMessageInboxOrder = 'desc';
const DEFAULT_MODE: DataTableMode = 'infinite';
const MESSAGE_SORTS: readonly string[] = [
    'latest',
    'urgency',
    'frustration',
    'waiting'
] satisfies DmMessageInboxSort[];

function isMessageSort(raw: string | null): raw is DmMessageInboxSort {
    return raw !== null && MESSAGE_SORTS.includes(raw);
}

export const CLOSED_MESSAGE_SORT_RULE: ClosedSortRule<DmMessageInboxSort> = {
    openOnly: ['urgency', 'frustration', 'waiting'],
    fallback: 'latest'
};

export function parseMessageSort(raw: string | null, status: DmInboxStatus): DmMessageInboxSort {
    const sort = isMessageSort(raw) ? raw : DEFAULT_MESSAGE_SORT;
    return sortForStatus(sort, status, CLOSED_MESSAGE_SORT_RULE);
}

export function parseMessageOrder(raw: string | null): DmMessageInboxOrder {
    return raw === 'asc' || raw === 'desc' ? raw : DEFAULT_ORDER;
}

export function parseMessageMode(raw: string | null): DataTableMode {
    return raw === 'paged' || raw === 'infinite' ? raw : DEFAULT_MODE;
}

// Defaults are written as "absent" so a fresh list keeps a bare URL.
export function messageListParamValue(
    key: MessageListParamKey,
    value: string | string[]
): string | null {
    const encoded = Array.isArray(value) ? encodeUrlArray(value) : value.trim();
    const defaults: Partial<Record<MessageListParamKey, string>> = {
        [MESSAGE_LIST_PARAM.sort]: DEFAULT_MESSAGE_SORT,
        [MESSAGE_LIST_PARAM.order]: DEFAULT_ORDER,
        [MESSAGE_LIST_PARAM.mode]: DEFAULT_MODE,
        [MESSAGE_LIST_PARAM.status]: DEFAULT_INBOX_STATUS
    };
    return encoded === '' || encoded === defaults[key] ? null : encoded;
}

// The list's filters as they sit in the URL; assignees and tags are UI values, not API ones.
export interface MessageListParams {
    search: string;
    practiceIds: string[];
    assignees: string[];
    tags: string[];
    status: DmInboxStatus;
    sort: DmMessageInboxSort;
    order: DmMessageInboxOrder;
}

export function readMessageListParams(searchParams: URLSearchParams): MessageListParams {
    const list = (key: string) => decodeUrlArray(searchParams.get(key) ?? '');
    const status = parseInboxStatus(searchParams.get(MESSAGE_LIST_PARAM.status));
    return {
        search: parseInboxSearch(searchParams.get(MESSAGE_LIST_PARAM.search)),
        practiceIds: list(MESSAGE_LIST_PARAM.practiceIds),
        assignees: list(MESSAGE_LIST_PARAM.assignees),
        tags: list(MESSAGE_LIST_PARAM.tags),
        status,
        sort: parseMessageSort(searchParams.get(MESSAGE_LIST_PARAM.sort), status),
        order: parseMessageOrder(searchParams.get(MESSAGE_LIST_PARAM.order))
    };
}

export function pickMessageListSearch(searchParams: URLSearchParams): string {
    return pickListSearch(searchParams, MESSAGE_LIST_PARAM_KEYS);
}

export function messageViewPath(patientId: string, listSearch: string): string {
    return `${MESSAGES_LIST_PATH}/${encodeURIComponent(patientId)}${listSearch}`;
}

function messageListPath(listSearch: string): string {
    return `${MESSAGES_LIST_PATH}${listSearch}`;
}

const FROM_LIST_FLAG = 'fromMessageList';

// Set by the list when it opens a view, so the view's Back pops back to Messages.
export const FROM_MESSAGE_LIST_STATE = { [FROM_LIST_FLAG]: true } as const;

export const MESSAGE_VIEW_ROUTES: ListViewRoutes = {
    listKeys: MESSAGE_LIST_PARAM_KEYS,
    fromListFlag: FROM_LIST_FLAG,
    listPath: messageListPath,
    viewPath: messageViewPath
};
